import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { LlmSummary } from "@egalregal/shared";
import { useT } from "../../shared/i18n";
import { useAppStore } from "../../shared/store/app";
import { MicIcon, RotateRightIcon } from "../../shared/components/icons";

/**
 * The "by AI" provenance marker (ticket 03): a slim chip next to a
 * description/owner that was filled by the AI summary. Clicking it expands a
 * details row showing the model, when it was generated, its sources, the
 * merged data and the transcript it was summarized from.
 *
 * `label: "block"` is the standalone variant the detail view uses when the AI
 * block exists but none of the scalar fields carry it (a manual description
 * wins, ADR-0028). It names the block instead of claiming a field is AI-filled.
 */
export interface AiSummaryMarkerProps {
  summary: LlmSummary;
  /** "field" marks a scalar the AI filled; "block" names the AI block itself. */
  label?: "field" | "block";
  /** Which scalar a field marker belongs to; gives every chip a unique name. */
  field?: "description" | "owner";
  /** Present when the expansion offers a manual re-extract (ticket 06). */
  onReExtract?: () => void;
  reExtracting?: boolean;
  /** Test hook: the field chip and the standalone block chip differ. */
  testId?: string;
}

export function AiSummaryMarker({
  summary,
  label = "field",
  field,
  onReExtract,
  reExtracting = false,
  testId = "ai-summary-marker",
}: AiSummaryMarkerProps) {
  const t = useT();
  const language = useAppStore((state) => state.language);
  const [open, setOpen] = useState(false);

  const sourceLabels: Record<LlmSummary["sources"][number]["kind"], string> = {
    description: t("aiSummarySourceDescription"),
    transcript: t("aiSummarySourceTranscript"),
    detection: t("aiSummarySourceDetection"),
  };
  const sources = summary.sources.map((source) => sourceLabels[source.kind]).join(", ");
  const markerLabel =
    label === "block"
      ? t("aiSummaryBlock")
      : field === "owner"
        ? t("aiSummaryFieldOwner")
        : field === "description"
          ? t("aiSummaryFieldDescription")
          : t("aiSummaryTitle");
  const generatedAt = new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(summary.generatedAt));

  return (
    <Box component="span" data-testid={testId}>
      <Chip
        icon={<MicIcon fontSize="small" />}
        label={label === "block" ? t("aiSummaryTitle") : t("aiSummaryByAi")}
        size="small"
        variant="outlined"
        color="info"
        onClick={() => setOpen((o) => !o)}
        aria-label={markerLabel}
        sx={{ ml: 1 }}
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 0.5,
            pl: 1,
            borderLeft: "2px solid",
            borderColor: "info.main",
          }}
        >
          <Stack spacing={0.25}>
            <Typography variant="caption" component="div">
              {t("aiSummaryModel", { model: summary.model })}
            </Typography>
            <Typography variant="caption" component="div">
              {t("aiSummaryGeneratedAt", { date: generatedAt })}
            </Typography>
            {sources && (
              <Typography variant="caption" component="div">
                {t("aiSummarySources")}: {sources}
              </Typography>
            )}
            <Box sx={{ mt: 0.5 }} data-testid="ai-summary-merged-data">
              <Typography variant="caption" component="div" sx={{ fontWeight: 500 }}>
                {t("aiSummaryMergedData")}
              </Typography>
              {summary.data.owner && (
                <Typography variant="body2">
                  {t("aiSummaryDataOwner", { owner: summary.data.owner })}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", fontSize: "0.8rem" }}
              >
                {summary.data.summary}
              </Typography>
            </Box>
            {summary.transcript && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" component="div" sx={{ fontWeight: 500 }}>
                  {t("aiSummaryTranscript")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap", fontSize: "0.8rem" }}
                >
                  {summary.transcript}
                </Typography>
              </Box>
            )}
            {onReExtract && (
              <Box sx={{ pt: 0.5 }}>
                <Button
                  size="small"
                  color="info"
                  startIcon={<RotateRightIcon fontSize="small" />}
                  disabled={reExtracting}
                  onClick={onReExtract}
                  aria-label={t("aiSummaryReExtractLabel")}
                  data-testid="ai-summary-re-extract"
                >
                  {reExtracting ? t("aiSummaryReExtracting") : t("aiSummaryReExtract")}
                </Button>
              </Box>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
